import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft } from 'lucide-react';

const MonitoringAnalytics = () => {
  const [activeUsers, setActiveUsers] = useState(247);
  const [reqPerMin, setReqPerMin] = useState(142);
  const [avgLatency, setAvgLatency] = useState('1.8s');
  const [cacheHit, setCacheHit] = useState('67%');
  const [timeRange, setTimeRange] = useState('24h');
  const [feedEntries, setFeedEntries] = useState([]);
  const feedContainerRef = useRef(null);

  // Update metrics every 5 seconds
  useEffect(() => {
    const updateMetrics = () => {
      setActiveUsers(Math.floor(200 + Math.random() * 100));
      setReqPerMin(Math.floor(120 + Math.random() * 40));
      setAvgLatency((1.5 + Math.random() * 0.8).toFixed(1) + 's');
      setCacheHit(Math.floor(60 + Math.random() * 15) + '%');
    };

    const interval = setInterval(updateMetrics, 5000);
    return () => clearInterval(interval);
  }, []);

  // Generate response time chart data
  const generateChartData = () => {
    return Array.from({ length: 24 }, () => ({
      height: 20 + Math.random() * 70,
      value: (1.0 + Math.random() * 2.0).toFixed(1)
    }));
  };

  const [chartData, setChartData] = useState(generateChartData());

  // Update chart when time range changes
  useEffect(() => {
    setChartData(generateChartData());
  }, [timeRange]);

  // Generate heatmap data
  const generateHeatmapData = () => {
    return Array.from({ length: 24 }, (_, i) => {
      const intensity = Math.random();
      let level = 0;
      if (intensity > 0.8) level = 5;
      else if (intensity > 0.6) level = 4;
      else if (intensity > 0.4) level = 3;
      else if (intensity > 0.2) level = 2;
      else if (intensity > 0.1) level = 1;
      
      return {
        level,
        title: `${i}:00 - ${Math.floor(intensity * 1000)} requêtes`
      };
    });
  };

  const [heatmapData, setHeatmapData] = useState(generateHeatmapData());

  // Update heatmap every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setHeatmapData(generateHeatmapData());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Add live feed entries
  useEffect(() => {
    const addFeedEntry = () => {
      const time = new Date().toLocaleTimeString();
      const users = ['alice.martin', 'bob.dupont', 'claire.bernard', 'david.moreau'];
      const actions = ['QUERY', 'FEEDBACK', 'LOGIN', 'SEARCH'];
      const user = users[Math.floor(Math.random() * users.length)];
      const action = actions[Math.floor(Math.random() * actions.length)];
      
      const details = action === 'QUERY' ? 'chèque impayé BEAC' :
                     action === 'FEEDBACK' ? 'rating: 5/5' :
                     action === 'LOGIN' ? 'BackOffice profile' :
                     'vector search';
      
      const newEntry = { time, user, action, details, id: Date.now() };
      
      setFeedEntries(prev => [newEntry, ...prev].slice(0, 20));
    };

    // Initial feed entries
    for (let i = 0; i < 10; i++) {
      setTimeout(() => addFeedEntry(), i * 200);
    }

    const interval = setInterval(addFeedEntry, 2000);
    return () => clearInterval(interval);
  }, []);

  // Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.chart-container, .metric-card, .alert-item').forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
      el.style.transition = 'all 0.6s ease-out';
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  const exportData = (format) => {
    alert(`Export ${format.toUpperCase()} initié. Le fichier sera téléchargé dans quelques instants.`);
  };

  const scheduledReport = () => {
    alert('Configuration du rapport programmé. Un email sera envoyé chaque lundi à 9h00.');
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Status Bar */
        .status-bar {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 1.5rem;
            margin-bottom: 3rem;
            display: flex;
            justify-content: space-around;
            align-items: center;
            flex-wrap: wrap;
            gap: 2rem;
        }
        
        .status-item {
            text-align: center;
        }
        
        .status-indicator {
            width: 12px;
            height: 12px;
            background: #67e8a9;
            border-radius: 50%;
            display: inline-block;
            margin-right: 0.5rem;
            animation: pulse 2s ease-in-out infinite;
        }
        
        .status-indicator.warning {
            background: #ed8936;
        }
        
        .status-indicator.error {
            background: #f56565;
        }
        
        @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.2); }
        }
        
        .status-label {
            font-weight: 600;
            margin-bottom: 0.3rem;
        }
        
        .status-value {
            font-size: 1.5rem;
            color: #667eea;
            font-weight: bold;
        }
        
        /* Dashboard Grid */
        .dashboard-grid {
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 2rem;
            margin-bottom: 3rem;
        }
        
        .dashboard-main {
            display: flex;
            flex-direction: column;
            gap: 2rem;
        }
        
        /* Chart Containers */
        .chart-container {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            transition: all 0.3s;
        }
        
        .chart-container:hover {
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .chart-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
        }
        
        .chart-title {
            font-size: 1.3rem;
            font-weight: 600;
        }
        
        .chart-controls {
            display: flex;
            gap: 0.5rem;
        }
        
        .time-selector {
            padding: 0.4rem 1rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 6px;
            color: white;
            cursor: pointer;
            transition: all 0.3s;
            font-size: 0.9rem;
        }
        
        .time-selector:hover {
            background: rgba(255, 255, 255, 0.1);
        }
        
        .time-selector.active {
            background: rgba(102, 126, 234, 0.2);
            border-color: rgba(102, 126, 234, 0.5);
        }
        
        /* Chart Area */
        .chart-area {
            height: 300px;
            position: relative;
            margin-bottom: 1rem;
            background: rgba(255, 255, 255, 0.02);
            border-radius: 8px;
            overflow: hidden;
        }
        
        .chart-bars {
            display: flex;
            align-items: flex-end;
            height: 100%;
            padding: 1rem;
            gap: 0.5rem;
        }
        
        .chart-bar {
            flex: 1;
            background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
            border-radius: 4px 4px 0 0;
            position: relative;
            transition: all 0.3s;
            cursor: pointer;
            min-height: 10px;
        }
        
        .chart-bar:hover {
            opacity: 0.8;
            transform: scaleY(1.05);
        }
        
        .chart-bar-value {
            position: absolute;
            top: -25px;
            left: 50%;
            transform: translateX(-50%);
            font-size: 0.8rem;
            font-weight: bold;
            opacity: 0;
            transition: opacity 0.3s;
            background: rgba(0, 0, 0, 0.8);
            padding: 0.2rem 0.5rem;
            border-radius: 4px;
            white-space: nowrap;
        }
        
        .chart-bar:hover .chart-bar-value {
            opacity: 1;
        }
        
        .chart-labels {
            display: flex;
            justify-content: space-between;
            color: #888;
            font-size: 0.8rem;
        }
        
        /* Metrics Grid */
        .metrics-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1.5rem;
        }
        
        .metric-card {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 1.5rem;
            text-align: center;
            transition: all 0.3s;
        }
        
        .metric-card:hover {
            transform: scale(1.05);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .metric-value {
            font-size: 2rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin-bottom: 0.5rem;
        }
        
        .metric-label {
            color: #888;
            font-size: 0.9rem;
        }
        
        .metric-trend {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.3rem;
            margin-top: 0.5rem;
            font-size: 0.8rem;
        }
        
        .trend-up { color: #67e8a9; }
        .trend-down { color: #f56565; }
        .trend-stable { color: #888; }
        
        /* Alerts Section */
        .alerts-section {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin-bottom: 2rem;
        }
        
        .alerts-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
        }
        
        .alert-item {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 1rem;
            margin-bottom: 1rem;
            border-radius: 8px;
            transition: all 0.3s;
        }
        
        .alert-item:hover {
            background: rgba(255, 255, 255, 0.05);
        }
        
        .alert-item:last-child {
            margin-bottom: 0;
        }
        
        .alert-critical {
            background: rgba(245, 101, 101, 0.1);
            border: 1px solid rgba(245, 101, 101, 0.3);
        }
        
        .alert-warning {
            background: rgba(237, 137, 54, 0.1);
            border: 1px solid rgba(237, 137, 54, 0.3);
        }
        
        .alert-info {
            background: rgba(102, 126, 234, 0.1);
            border: 1px solid rgba(102, 126, 234, 0.3);
        }
        
        .alert-icon {
            width: 40px;
            height: 40px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.2rem;
            flex-shrink: 0;
        }
        
        .alert-critical .alert-icon {
            background: rgba(245, 101, 101, 0.2);
            color: #f56565;
        }
        
        .alert-warning .alert-icon {
            background: rgba(237, 137, 54, 0.2);
            color: #ed8936;
        }
        
        .alert-info .alert-icon {
            background: rgba(102, 126, 234, 0.2);
            color: #667eea;
        }
        
        .alert-content {
            flex: 1;
        }
        
        .alert-title {
            font-weight: 600;
            margin-bottom: 0.3rem;
        }
        
        .alert-message {
            color: #bbb;
            font-size: 0.9rem;
        }
        
        .alert-time {
            color: #888;
            font-size: 0.8rem;
            text-align: right;
        }
        
        /* User Analytics */
        .user-analytics {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin: 2rem 0;
        }
        
        .user-stats {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 1rem;
            margin-bottom: 2rem;
        }
        
        .user-stat {
            background: rgba(255, 255, 255, 0.05);
            padding: 1rem;
            border-radius: 8px;
            text-align: center;
        }
        
        .user-stat-value {
            font-size: 1.5rem;
            font-weight: bold;
            color: #667eea;
        }
        
        .user-stat-label {
            font-size: 0.8rem;
            color: #888;
            margin-top: 0.3rem;
        }
        
        /* Query Analysis */
        .query-analysis {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
            margin: 2rem 0;
        }
        
        .popular-queries {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
        }
        
        .query-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1rem 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .query-item:last-child {
            border-bottom: none;
        }
        
        .query-text {
            flex: 1;
            font-size: 0.9rem;
            color: #bbb;
        }
        
        .query-count {
            color: #667eea;
            font-weight: bold;
        }
        
        /* Performance Trends */
        .performance-trends {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
        }
        
        .trend-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.8rem 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .trend-item:last-child {
            border-bottom: none;
        }
        
        .trend-metric {
            font-weight: 600;
        }
        
        .trend-value {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        
        /* Live Feed */
        .live-feed {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin: 2rem 0;
        }
        
        .feed-container {
            height: 400px;
            overflow-y: auto;
            background: #1a1a1a;
            border-radius: 8px;
            padding: 1rem;
            font-family: monospace;
            font-size: 0.9rem;
        }
        
        .feed-entry {
            padding: 0.5rem 0;
            border-bottom: 1px solid #333;
            opacity: 0;
            animation: fadeIn 0.3s ease forwards;
        }
        
        .feed-entry:last-child {
            border-bottom: none;
        }
        
        .feed-time {
            color: #667eea;
            margin-right: 1rem;
        }
        
        .feed-user {
            color: #82aaff;
            margin-right: 1rem;
        }
        
        .feed-action {
            color: #c3e88d;
            margin-right: 1rem;
        }
        
        .feed-details {
            color: #e0e0e0;
        }
        
        /* Heatmap */
        .heatmap-container {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin: 2rem 0;
        }
        
        .heatmap-grid {
            display: grid;
            grid-template-columns: repeat(24, 1fr);
            gap: 2px;
            margin: 2rem 0;
        }
        
        .heatmap-cell {
            aspect-ratio: 1;
            border-radius: 2px;
            transition: all 0.3s;
            cursor: pointer;
        }
        
        .heatmap-cell:hover {
            transform: scale(1.2);
            border: 1px solid rgba(102, 126, 234, 0.5);
        }
        
        .heat-level-0 { background: rgba(255, 255, 255, 0.05); }
        .heat-level-1 { background: rgba(102, 126, 234, 0.2); }
        .heat-level-2 { background: rgba(102, 126, 234, 0.4); }
        .heat-level-3 { background: rgba(102, 126, 234, 0.6); }
        .heat-level-4 { background: rgba(102, 126, 234, 0.8); }
        .heat-level-5 { background: rgba(245, 101, 101, 0.8); }
        
        .heatmap-legend {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 1rem;
            margin-top: 1rem;
        }
        
        .legend-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.8rem;
            color: #888;
        }
        
        .legend-color {
            width: 12px;
            height: 12px;
            border-radius: 2px;
        }
        
        /* Export Controls */
        .export-controls {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin: 2rem 0;
            text-align: center;
        }
        
        .export-buttons {
            display: flex;
            gap: 1rem;
            justify-content: center;
            margin-top: 1rem;
            flex-wrap: wrap;
        }
        
        .export-button {
            padding: 0.8rem 1.5rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 600;
            transition: all 0.3s;
        }
        
        .export-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        .export-button.secondary {
            background: transparent;
            border: 2px solid #667eea;
            color: #667eea;
        }
        
        /* Animations */
        @keyframes fadeIn {
            to {
                opacity: 1;
            }
        }
        
        /* Responsive */
        @media (max-width: 1024px) {
            .dashboard-grid {
                grid-template-columns: 1fr;
            }
            
            .query-analysis {
                grid-template-columns: 1fr;
            }
            
            .user-stats {
                grid-template-columns: repeat(2, 1fr);
            }
        }
        
        @media (max-width: 768px) {
            .status-bar {
                flex-direction: column;
            }
            
            .metrics-grid {
                grid-template-columns: 1fr;
            }
            
            .heatmap-grid {
                grid-template-columns: repeat(12, 1fr);
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Monitoring et Analytics</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Monitoring et Analytics</h1>
          <p className="page-subtitle">Supervision temps réel et analytics de l'Agent RAG Hybride</p>
        </div>
        
        {/* Status Bar */}
        <div className="status-bar">
          <div className="status-item">
            <div className="status-label">
              <span className="status-indicator"></span>
              System Status
            </div>
            <div className="status-value">Operational</div>
          </div>
          <div className="status-item">
            <div className="status-label">Utilisateurs Actifs</div>
            <div className="status-value">{activeUsers}</div>
          </div>
          <div className="status-item">
            <div className="status-label">Requêtes/min</div>
            <div className="status-value">{reqPerMin}</div>
          </div>
          <div className="status-item">
            <div className="status-label">Latence Moy.</div>
            <div className="status-value">{avgLatency}</div>
          </div>
          <div className="status-item">
            <div className="status-label">Cache Hit Rate</div>
            <div className="status-value">{cacheHit}</div>
          </div>
          <div className="status-item">
            <div className="status-label">Uptime</div>
            <div className="status-value">99.97%</div>
          </div>
        </div>
        
        {/* Dashboard Grid */}
        <div className="dashboard-grid">
          <div className="dashboard-main">
            {/* Response Time Chart */}
            <div className="chart-container">
              <div className="chart-header">
                <h3 className="chart-title">Temps de Réponse (Dernières 24h)</h3>
                <div className="chart-controls">
                  <button 
                    className={`time-selector ${timeRange === '24h' ? 'active' : ''}`}
                    onClick={() => handleTimeRangeChange('24h')}
                  >
                    24h
                  </button>
                  <button 
                    className={`time-selector ${timeRange === '7d' ? 'active' : ''}`}
                    onClick={() => handleTimeRangeChange('7d')}
                  >
                    7j
                  </button>
                  <button 
                    className={`time-selector ${timeRange === '30d' ? 'active' : ''}`}
                    onClick={() => handleTimeRangeChange('30d')}
                  >
                    30j
                  </button>
                </div>
              </div>
              <div className="chart-area">
                <div className="chart-bars">
                  {chartData.map((bar, index) => (
                    <div 
                      key={index}
                      className="chart-bar" 
                      style={{ height: `${bar.height}%` }}
                    >
                      <div className="chart-bar-value">{bar.value}s</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="chart-labels">
                <span>00:00</span>
                <span>06:00</span>
                <span>12:00</span>
                <span>18:00</span>
                <span>24:00</span>
              </div>
            </div>
            
            {/* User Analytics */}
            <div className="user-analytics">
              <h3 style={{marginBottom: '1.5rem'}}>Analytics Utilisateurs</h3>
              <div className="user-stats">
                <div className="user-stat">
                  <div className="user-stat-value">1,247</div>
                  <div className="user-stat-label">Sessions aujourd'hui</div>
                </div>
                <div className="user-stat">
                  <div className="user-stat-value">8.7 min</div>
                  <div className="user-stat-label">Durée moy. session</div>
                </div>
                <div className="user-stat">
                  <div className="user-stat-value">3.2</div>
                  <div className="user-stat-label">Requêtes/session</div>
                </div>
                <div className="user-stat">
                  <div className="user-stat-value">89%</div>
                  <div className="user-stat-label">Taux satisfaction</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sidebar Metrics */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-value">95.2%</div>
              <div className="metric-label">Précision</div>
              <div className="metric-trend trend-up">
                ↑ +2.1% vs hier
              </div>
            </div>
            
            <div className="metric-card">
              <div className="metric-value">92.7%</div>
              <div className="metric-label">Rappel</div>
              <div className="metric-trend trend-stable">
                → Stable
              </div>
            </div>
            
            <div className="metric-card">
              <div className="metric-value">93.9%</div>
              <div className="metric-label">F1-Score</div>
              <div className="metric-trend trend-up">
                ↑ +1.8%
              </div>
            </div>
            
            <div className="metric-card">
              <div className="metric-value">45.2k</div>
              <div className="metric-label">Requêtes 24h</div>
              <div className="metric-trend trend-up">
                ↑ +12%
              </div>
            </div>
          </div>
        </div>
        
        {/* Alerts Section */}
        <div className="alerts-section">
          <div className="alerts-header">
            <h3>🚨 Alertes Actives</h3>
            <span style={{color: '#888', fontSize: '0.9rem'}}>Dernière mise à jour: il y a 2 min</span>
          </div>
          
          <div className="alert-item alert-warning">
            <div className="alert-icon">⚠️</div>
            <div className="alert-content">
              <div className="alert-title">Latence élevée détectée</div>
              <div className="alert-message">P95 > 3s sur l'endpoint /query depuis 15 minutes</div>
            </div>
            <div className="alert-time">14:32</div>
          </div>
          
          <div className="alert-item alert-info">
            <div className="alert-icon">💾</div>
            <div className="alert-content">
              <div className="alert-title">Cache Redis à 87%</div>
              <div className="alert-message">Recommandation: Scaling horizontal ou nettoyage</div>
            </div>
            <div className="alert-time">13:45</div>
          </div>
          
          <div className="alert-item alert-critical">
            <div className="alert-icon">🔥</div>
            <div className="alert-content">
              <div className="alert-title">Pic de trafic inhabituel</div>
              <div className="alert-message">+340% de requêtes vs moyenne, investigation en cours</div>
            </div>
            <div className="alert-time">13:18</div>
          </div>
        </div>
        
        {/* Query Analysis */}
        <div className="query-analysis">
          <div className="popular-queries">
            <h3 style={{marginBottom: '1.5rem'}}>Top 10 Requêtes</h3>
            
            <div className="query-item">
              <div className="query-text">Comment traiter un chèque impayé selon BEAC?</div>
              <div className="query-count">1,247</div>
            </div>
            
            <div className="query-item">
              <div className="query-text">Procédure ouverture compte entreprise</div>
              <div className="query-count">892</div>
            </div>
            
            <div className="query-item">
              <div className="query-text">Erreur ORA-12154 module paiement</div>
              <div className="query-count">673</div>
            </div>
            
            <div className="query-item">
              <div className="query-text">Limites virement international CEMAC</div>
              <div className="query-count">534</div>
            </div>
            
            <div className="query-item">
              <div className="query-text">Configuration serveur batch nuit</div>
              <div className="query-count">421</div>
            </div>
            
            <div className="query-item">
              <div className="query-text">Réglementation KYC entreprise 2024</div>
              <div className="query-count">387</div>
            </div>
            
            <div className="query-item">
              <div className="query-text">Procédure incident sécurité</div>
              <div className="query-count">312</div>
            </div>
            
            <div className="query-item">
              <div className="query-text">API SWIFT intégration MT103</div>
              <div className="query-count">289</div>
            </div>
            
            <div className="query-item">
              <div className="query-text">Backup base données procédure</div>
              <div className="query-count">247</div>
            </div>
            
            <div className="query-item">
              <div className="query-text">Module gub_cheque_reject documentation</div>
              <div className="query-count">198</div>
            </div>
          </div>
          
          <div className="performance-trends">
            <h3 style={{marginBottom: '1.5rem'}}>Tendances Performance</h3>
            
            <div className="trend-item">
              <div className="trend-metric">Latence P50</div>
              <div className="trend-value">
                <span>1.2s</span>
                <span className="trend-up">↑ 8%</span>
              </div>
            </div>
            
            <div className="trend-item">
              <div className="trend-metric">Latence P95</div>
              <div className="trend-value">
                <span>2.8s</span>
                <span className="trend-up">↑ 15%</span>
              </div>
            </div>
            
            <div className="trend-item">
              <div className="trend-metric">Cache Hit Rate</div>
              <div className="trend-value">
                <span>67%</span>
                <span className="trend-up">↑ 3%</span>
              </div>
            </div>
            
            <div className="trend-item">
              <div className="trend-metric">Taux d'erreur</div>
              <div className="trend-value">
                <span>0.3%</span>
                <span className="trend-down">↓ 12%</span>
              </div>
            </div>
            
            <div className="trend-item">
              <div className="trend-metric">CPU Utilisation</div>
              <div className="trend-value">
                <span>64%</span>
                <span className="trend-up">↑ 5%</span>
              </div>
            </div>
            
            <div className="trend-item">
              <div className="trend-metric">Memory Usage</div>
              <div className="trend-value">
                <span>78%</span>
                <span className="trend-stable">→</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Heatmap */}
        <div className="heatmap-container">
          <h3 style={{marginBottom: '1rem'}}>Heatmap d'Activité (24h)</h3>
          <div className="heatmap-grid">
            {heatmapData.map((cell, index) => (
              <div 
                key={index}
                className={`heatmap-cell heat-level-${cell.level}`}
                title={cell.title}
              />
            ))}
          </div>
          <div className="heatmap-legend">
            <div className="legend-item">
              <div className="legend-color heat-level-0"></div>
              <span>Faible</span>
            </div>
            <div className="legend-item">
              <div className="legend-color heat-level-2"></div>
              <span>Moyen</span>
            </div>
            <div className="legend-item">
              <div className="legend-color heat-level-4"></div>
              <span>Élevé</span>
            </div>
            <div className="legend-item">
              <div className="legend-color heat-level-5"></div>
              <span>Critique</span>
            </div>
          </div>
        </div>
        
        {/* Live Feed */}
        <div className="live-feed">
          <h3 style={{marginBottom: '1rem'}}>Feed Temps Réel</h3>
          <div className="feed-container" ref={feedContainerRef}>
            {feedEntries.map(entry => (
              <div key={entry.id} className="feed-entry">
                <span className="feed-time">{entry.time}</span>
                <span className="feed-user">{entry.user}</span>
                <span className="feed-action">{entry.action}</span>
                <span className="feed-details">{entry.details}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Export Controls */}
        <div className="export-controls">
          <h3 style={{marginBottom: '1rem'}}>Export des Données</h3>
          <p style={{color: '#888', marginBottom: '1.5rem'}}>
            Exportez les métriques et analytics pour analyse externe ou archivage.
          </p>
          <div className="export-buttons">
            <button className="export-button" onClick={() => exportData('pdf')}>
              📄 Export PDF
            </button>
            <button className="export-button secondary" onClick={() => exportData('csv')}>
              📊 Export CSV
            </button>
            <button className="export-button secondary" onClick={() => exportData('json')}>
              💾 Export JSON
            </button>
            <button className="export-button secondary" onClick={scheduledReport}>
              ⏰ Rapport Programmé
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MonitoringAnalytics;